<script setup lang="ts">
import { ClipboardCheckIcon, ClipboardCopyIcon } from '@heroicons/vue/outline';
import { DownloadIcon } from '@heroicons/vue/solid';
import type { PropType } from 'vue';
import { useGoby } from '~/composables/useGoby';
import { getApiRoot } from '~/utilities/api';
import { downloadOffer } from '~/utilities/offer-handling';

const props = defineProps({
  compact: { type: Boolean, default: false },
  offer: { type: Object as PropType<any>, required: true },
  nft: { type: Object as PropType<any>, required: true },
});
const { compact, offer, nft } = toRefs(props);

const offerBech32 = await $fetch<any>(`${getApiRoot()}/offers/${offer.value.id}/bech32`);

const { goby, takeOffer: takeOfferInGoby } = useGoby();

const copied = ref(false);
const copyToClipboard = async () => {
  useTrackEvent('offer_copy');
  if (offerBech32) {
    try {
      // Try the modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(offerBech32);
      } else {
        // Fallback for iOS Safari
        const textArea = document.createElement('textarea');
        textArea.value = offerBech32;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }
      copied.value = true;
      setTimeout(() => (copied.value = false), 1000);
    } catch (e) {
      console.error('Failed to copy text:', e);
    }
  }
};
const getAndTakeOfferInGoby = async () => {
  if (offerBech32) {
    await takeOfferInGoby(offerBech32);
  }
};
</script>

<template>
  <div
    class="grid grid-cols-1 items-center"
    :class="[compact ? 'gap-2' : 'gap-4', offer.contains_mint_spend ? 'lg:grid-cols-3' : 'lg:grid-cols-4']"
  >
    <button
      :class="[compact ? 'px-2' : 'px-4']"
      :title="$t('offers.copyOffer')"
      class="shadow-attribute betterhover:hover:shadow-card-hover flex w-full items-center justify-center rounded-full border border-transparent bg-white py-2 text-center text-base font-medium text-black transition duration-100 ease-in-out dark:bg-black dark:text-white betterhover:hover:-translate-y-0.5"
      @click.prevent="copyToClipboard()"
    >
      <ClipboardCheckIcon v-if="copied" :class="[compact ? 'h-5 w-5' : '-ml-1 mr-2 h-6 w-6']" />
      <ClipboardCopyIcon v-else :class="[compact ? 'h-5 w-5' : '-ml-1 mr-2 h-6 w-6']" />
      <template v-if="!compact">{{ $t('offers.copyOffer') }}</template>
    </button>
    <button
      :class="[compact ? 'px-2' : 'px-4']"
      :title="$t('offers.downloadOffer')"
      class="shadow-attribute betterhover:hover:shadow-card-hover flex w-full items-center justify-center rounded-full border border-transparent bg-white py-2 text-center text-base font-medium text-black transition duration-100 ease-in-out dark:bg-black dark:text-white betterhover:hover:-translate-y-0.5"
      @click.prevent="downloadOffer(offer, nft)"
    >
      <DownloadIcon :class="[compact ? 'h-5 w-5' : '-ml-1 mr-2 h-6 w-6']" />
      <template v-if="!compact">{{ $t('offers.downloadOffer') }}</template>
    </button>
    <DetailsWalletConnect v-if="!offer.contains_mint_spend" :offer="offer" :nft="nft" :compact="compact" />
    <button
      v-if="goby.isGobyInstalled"
      type="button"
      class="betterhover:hover:shadow-card-hover flex w-full justify-center rounded-full border border-transparent bg-green-600 py-2 font-medium text-white transition duration-100 ease-in-out betterhover:hover:-translate-y-0.5"
      @click="getAndTakeOfferInGoby()"
    >
      <img :class="[compact ? 'h-5 w-5' : '-ml-1 mr-2 h-6 w-6']" aria-hidden="true" src="/goby-logo-white.svg" />
      <template v-if="!compact">{{ $t('offers.takeOffer') }}</template>
    </button>
  </div>
</template>
